import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate, NavLink, Routes, Route, Outlet } from "react-router-dom";
import api from "@/lib/api";
import { SERVICES, JOB_ROLES } from "@/lib/api";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, LineChart, Line, CartesianGrid } from "recharts";
import { ChartBar, Users, Briefcase, CalendarCheck, Bell, House, SignOut, Trash, Calendar as CalendarIcon } from "@phosphor-icons/react";

export default function AdminLayout() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (ready) {
      if (!user) nav("/login", { state: { from: "/admin" } });
      else if (user.role !== "admin") nav("/dashboard");
    }
  }, [ready, user, nav]);

  if (!user || user.role !== "admin") return null;

  return (
    <div className="theme-dark bg-[#0A0A0A] text-white min-h-screen" data-testid="admin-layout">
      <div className="grid grid-cols-12 min-h-screen">
        <aside className="col-span-12 md:col-span-2 border-r border-white/10 p-6 sticky top-0 h-auto md:h-screen">
          <div className="flex items-center gap-3 mb-10">
            <img src="/twinstars-logo.png" alt="Twinstars Plaza" className="h-12 w-auto object-contain" />
            <div>
              <p className="font-mono text-[10px] text-white/40">ADMIN CONSOLE</p>
            </div>
          </div>
          <nav className="space-y-1">
            <NavItem to="/admin" icon={<ChartBar size={16}/>} label="Overview" end />
            <NavItem to="/admin/bookings" icon={<CalendarCheck size={16}/>} label="Bookings" />
            <NavItem to="/admin/jobs" icon={<Briefcase size={16}/>} label="Applications" />
            <NavItem to="/admin/events" icon={<CalendarIcon size={16}/>} label="Events" />
            <NavItem to="/admin/users" icon={<Users size={16}/>} label="Users" />
            <NavItem to="/admin/notify" icon={<Bell size={16}/>} label="Notifications" />
          </nav>
          <div className="absolute bottom-6 left-6 right-6 space-y-2">
            <button onClick={() => nav("/")} className="flex items-center gap-2 text-xs text-white/60 hover:text-white"><House size={14}/> Back to site</button>
            <button onClick={async () => { await logout(); nav("/"); }} className="flex items-center gap-2 text-xs text-white/60 hover:text-white" data-testid="admin-logout"><SignOut size={14}/> Sign out</button>
          </div>
        </aside>
        <section className="col-span-12 md:col-span-10 p-6 md:p-10">
          <Outlet />
        </section>
      </div>
    </div>
  );
}

function NavItem({ to, icon, label, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={`admin-nav-${label.toLowerCase()}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 text-sm border ${isActive ? "bg-white text-black border-white" : "border-transparent text-white/70 hover:text-white hover:border-white/20"}`
      }
    >
      {icon} {label}
    </NavLink>
  );
}

export function AdminOverview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setStats(r.data)); }, []);
  if (!stats) return <p className="font-mono text-xs text-white/60">Loading…</p>;

  return (
    <div className="space-y-8" data-testid="admin-overview">
      <div>
        <p className="label-eyebrow text-white/40">Overview</p>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-2">Operations console</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="Total Revenue" value={`GHS ${stats.total_revenue.toFixed(2)}`} accent="#FF4444" testid="kpi-revenue" />
        <KPI label="Total bookings" value={stats.total_bookings} testid="kpi-bookings" />
        <KPI label="Pending bookings" value={stats.pending_bookings} testid="kpi-pending-bookings" />
        <KPI label="Applications" value={stats.total_jobs} testid="kpi-jobs" />
        <KPI label="Members" value={stats.total_users} testid="kpi-users" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-7 border border-white/10 bg-[#141414] p-6">
          <p className="label-eyebrow text-white/40">Bookings — last 7 days</p>
          <div className="h-72 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.daily_bookings}>
                <CartesianGrid stroke="#262626" />
                <XAxis dataKey="date" stroke="#737373" fontSize={11} />
                <YAxis stroke="#737373" fontSize={11} />
                <Tooltip contentStyle={{ background:"#0A0A0A", border:"1px solid #262626", borderRadius:0, color:"#fff" }}/>
                <Line type="monotone" dataKey="count" stroke="#FF4444" strokeWidth={2} dot={{ fill: "#FF4444" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="md:col-span-5 border border-white/10 bg-[#141414] p-6">
          <p className="label-eyebrow text-white/40">By service</p>
          <div className="h-72 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.by_service}>
                <CartesianGrid stroke="#262626" />
                <XAxis dataKey="service" stroke="#737373" fontSize={11} />
                <YAxis stroke="#737373" fontSize={11} />
                <Tooltip contentStyle={{ background:"#0A0A0A", border:"1px solid #262626", borderRadius:0, color:"#fff" }} />
                <Bar dataKey="count" fill="#3366FF" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, accent, testid }) {
  return (
    <div className="border border-white/10 bg-[#141414] p-5" data-testid={testid}>
      <p className="label-eyebrow text-white/40">{label}</p>
      <p className="font-display text-3xl font-black mt-2" style={{ color: accent || "#fff" }}>{value}</p>
    </div>
  );
}

export function AdminBookings() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ service: "", status: "" });
  const load = async () => {
    const q = new URLSearchParams();
    if (filter.service) q.set("service", filter.service);
    if (filter.status) q.set("status", filter.status);
    const { data } = await api.get(`/bookings?${q.toString()}`);
    setItems(data);
  };
  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}/status`, { status });
      toast.success(`Booking ${status}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm(`Delete booking #${id.slice(-8).toUpperCase()}? This will refund any wallet credit used. This cannot be undone.`)) return;
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Booking deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-bookings">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="label-eyebrow text-white/40">Bookings</p>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight mt-2">All bookings</h1>
        </div>
        <div className="flex gap-2">
          <select className="input-sharp !w-auto" value={filter.service} onChange={(e)=>setFilter({...filter, service:e.target.value})} data-testid="filter-service">
            <option value="">All services</option>
            {SERVICES.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
          </select>
          <select className="input-sharp !w-auto" value={filter.status} onChange={(e)=>setFilter({...filter, status:e.target.value})} data-testid="filter-status">
            <option value="">All statuses</option>
            {["pending","approved","completed","rejected","cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div className="border border-white/10 bg-[#141414] overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-[#0A0A0A] text-white/60">
            <tr>
              <Th>Ref</Th><Th>User</Th><Th>Service</Th><Th>Amount</Th><Th>Status</Th><Th>Payment</Th><Th>Date</Th><Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.id} className="border-t border-white/10" data-testid={`admin-booking-${b.id}`}>
                <Td className="font-mono">#{b.id.slice(-8).toUpperCase()}</Td>
                <Td>{b.user_name}<br/><span className="text-xs text-white/40">{b.user_email}</span></Td>
                <Td className="capitalize">{b.service}</Td>
                <Td>GHS {(b.amount||0).toFixed(2)}</Td>
                <Td><span className="px-2 py-1 text-xs border border-white/20">{b.status}</span></Td>
                <Td className="text-xs">{b.payment_status}<br/><span className="text-white/40 font-mono text-[10px]">{b.payment_reference}</span></Td>
                <Td className="font-mono text-xs">{b.created_at?.slice(0,10)}</Td>
                <Td>
                  <div className="flex flex-wrap gap-1">
                    <button onClick={()=>updateStatus(b.id,"approved")} className="text-[11px] px-2 py-1 border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-black" data-testid={`btn-approve-${b.id}`}>Approve</button>
                    <button onClick={()=>updateStatus(b.id,"completed")} className="text-[11px] px-2 py-1 border border-blue-500 text-blue-400 hover:bg-blue-500 hover:text-black" data-testid={`btn-complete-${b.id}`}>Complete</button>
                    <button onClick={()=>updateStatus(b.id,"rejected")} className="text-[11px] px-2 py-1 border border-red-500 text-red-400 hover:bg-red-500 hover:text-white" data-testid={`btn-reject-${b.id}`}>Reject</button>
                    <button onClick={()=>deleteBooking(b.id)} className="text-[11px] px-2 py-1 border border-white/30 text-white/60 hover:bg-white hover:text-black inline-flex items-center gap-1" data-testid={`btn-delete-booking-${b.id}`} title="Delete booking">
                      <Trash size={12} /> Delete
                    </button>
                  </div>
                </Td>
              </tr>
            ))}
            {items.length === 0 && <tr><Td className="text-white/40" colSpan={8}>No bookings yet.</Td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminJobs() {
  const [items, setItems] = useState([]);
  const [role, setRole] = useState("");
  const load = async () => {
    const q = new URLSearchParams();
    if (role) q.set("role", role);
    const { data } = await api.get(`/jobs?${q.toString()}`);
    setItems(data);
  };
  useEffect(() => { load(); }, [role]);

  const updateStatus = async (id, status) => {
    try { await api.patch(`/jobs/${id}/status`, { status }); toast.success(`Application ${status}`); load(); }
    catch { toast.error("Update failed"); }
  };

  const deleteJob = async (id) => {
    if (!window.confirm("Delete this application? This cannot be undone.")) return;
    try {
      await api.delete(`/jobs/${id}`);
      toast.success("Application deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-jobs">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="label-eyebrow text-white/40">Careers</p>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight mt-2">Job applications</h1>
        </div>
        <select className="input-sharp !w-auto" value={role} onChange={(e)=>setRole(e.target.value)} data-testid="filter-role">
          <option value="">All roles</option>
          {JOB_ROLES.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
        </select>
      </div>
      <div className="border border-white/10 bg-[#141414] overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-[#0A0A0A] text-white/60"><tr><Th>Applicant</Th><Th>Role</Th><Th>Phone</Th><Th>Email</Th><Th>Status</Th><Th>Date</Th><Th>Actions</Th></tr></thead>
          <tbody>
            {items.map((j) => {
              const d = j.details || {};
              return (
                <tr key={j.id} className="border-t border-white/10" data-testid={`admin-job-${j.id}`}>
                  <Td>{d.full_name || "—"}</Td>
                  <Td className="capitalize">{j.role?.replace(/_/g, " ")}</Td>
                  <Td className="font-mono text-xs">{d.phone || "—"}</Td>
                  <Td className="text-xs">{d.email || "—"}</Td>
                  <Td><span className="px-2 py-1 text-xs border border-white/20">{j.status}</span></Td>
                  <Td className="font-mono text-xs">{j.created_at?.slice(0,10)}</Td>
                  <Td>
                    <div className="flex flex-wrap gap-1">
                      <button onClick={()=>updateStatus(j.id,"approved")} className="text-[11px] px-2 py-1 border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-black">Approve</button>
                      <button onClick={()=>updateStatus(j.id,"rejected")} className="text-[11px] px-2 py-1 border border-red-500 text-red-400 hover:bg-red-500 hover:text-white">Reject</button>
                      <button onClick={()=>deleteJob(j.id)} className="text-[11px] px-2 py-1 border border-white/30 text-white/60 hover:bg-white hover:text-black inline-flex items-center gap-1" data-testid={`btn-delete-job-${j.id}`} title="Delete application">
                        <Trash size={12} /> Delete
                      </button>
                    </div>
                  </Td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><Td className="text-white/40" colSpan={7}>No applications yet.</Td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminUsers() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/users").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete user "${u.name}" (${u.email})? This will also remove their bookings, applications, reviews and wallet history. This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-users">
      <div><p className="label-eyebrow text-white/40">Members</p><h1 className="font-display text-3xl md:text-4xl font-black tracking-tight mt-2">Users</h1></div>
      <div className="border border-white/10 bg-[#141414] overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-[#0A0A0A] text-white/60"><tr><Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Phone</Th><Th>Joined</Th><Th>Actions</Th></tr></thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-t border-white/10" data-testid={`admin-user-${u.id}`}>
                <Td>{u.name}</Td>
                <Td>{u.email}</Td>
                <Td className="capitalize">{u.role}</Td>
                <Td>{u.phone || "—"}</Td>
                <Td className="font-mono text-xs">{u.created_at?.slice(0,10)}</Td>
                <Td>
                  {u.role === "admin" ? (
                    <span className="text-[11px] text-white/30 font-mono">— protected —</span>
                  ) : (
                    <button
                      onClick={() => deleteUser(u)}
                      className="text-[11px] px-2 py-1 border border-white/30 text-white/60 hover:bg-white hover:text-black inline-flex items-center gap-1"
                      data-testid={`btn-delete-user-${u.id}`}
                      title="Delete user"
                    >
                      <Trash size={12} /> Delete
                    </button>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminNotify() {
  const [form, setForm] = useState({ title: "", message: "", audience: "all" });
  const [sending, setSending] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/admin/notify", form);
      toast.success("Notification sent");
      setForm({ title: "", message: "", audience: "all" });
    } catch { toast.error("Failed"); } finally { setSending(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-notify">
      <div><p className="label-eyebrow text-white/40">Notifications</p><h1 className="font-display text-3xl md:text-4xl font-black tracking-tight mt-2">Broadcast to members</h1></div>
      <form onSubmit={send} className="border border-white/10 bg-[#141414] p-6 space-y-4">
        <div>
          <label className="label-eyebrow text-white/40 block mb-2">Title</label>
          <input className="input-sharp" required value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} data-testid="notify-title" />
        </div>
        <div>
          <label className="label-eyebrow text-white/40 block mb-2">Message</label>
          <textarea rows={5} className="input-sharp" required value={form.message} onChange={(e)=>setForm({...form, message:e.target.value})} data-testid="notify-message" />
        </div>
        <div>
          <label className="label-eyebrow text-white/40 block mb-2">Audience</label>
          <select className="input-sharp" value={form.audience} onChange={(e)=>setForm({...form, audience:e.target.value})} data-testid="notify-audience">
            <option value="all">All customers</option>
            <option value="customers">Customers only</option>
          </select>
        </div>
        <button className="btn-primary" disabled={sending} data-testid="notify-send" style={{ background:"#fff", color:"#000", borderColor:"#fff" }}>
          {sending ? "Sending…" : "Send notification"}
        </button>
      </form>
    </div>
  );
}

export function AdminEvents() {
  const [items, setItems] = useState([]);
  const blank = { title: "", service: "boxing", event_date: "", venue: "", description: "", poster_url: "", ticket_from: "" };
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/events?upcoming=false").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      ticket_from: form.ticket_from === "" ? null : parseFloat(form.ticket_from),
    };
    try {
      if (editingId) {
        await api.patch(`/events/${editingId}`, payload);
        toast.success("Event updated");
      } else {
        await api.post("/events", payload);
        toast.success("Event added");
      }
      setForm(blank);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const edit = (ev) => {
    setEditingId(ev.id);
    setForm({
      title: ev.title || "",
      service: ev.service || "boxing",
      event_date: (ev.event_date || "").slice(0, 16),
      venue: ev.venue || "",
      description: ev.description || "",
      poster_url: ev.poster_url || "",
      ticket_from: ev.ticket_from != null ? String(ev.ticket_from) : "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (ev) => {
    if (!window.confirm(`Delete event "${ev.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/events/${ev.id}`);
      toast.success("Event deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-events">
      <div>
        <p className="label-eyebrow text-white/40">Events</p>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight mt-2">Manage fight nights & events</h1>
      </div>

      <form onSubmit={save} className="border border-white/10 bg-[#141414] p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2 flex items-center justify-between">
          <p className="label-eyebrow text-white/40">{editingId ? "Edit event" : "Add new event"}</p>
          {editingId && (
            <button type="button" onClick={() => { setEditingId(null); setForm(blank); }} className="text-xs text-white/60 underline">
              Cancel edit
            </button>
          )}
        </div>
        <Inp label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="event-title" required />
        <div>
          <label className="label-eyebrow text-white/40 block mb-2">Service</label>
          <select className="input-sharp" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} data-testid="event-service">
            {SERVICES.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
          </select>
        </div>
        <Inp label="Date & time" type="datetime-local" value={form.event_date} onChange={(v) => setForm({ ...form, event_date: v })} testid="event-date" required />
        <Inp label="Venue" value={form.venue} onChange={(v) => setForm({ ...form, venue: v })} testid="event-venue" />
        <Inp label="Poster image URL (optional)" value={form.poster_url} onChange={(v) => setForm({ ...form, poster_url: v })} testid="event-poster" />
        <Inp label="Tickets from (GHS)" type="number" step="0.01" value={form.ticket_from} onChange={(v) => setForm({ ...form, ticket_from: v })} testid="event-price" />
        <div className="md:col-span-2">
          <label className="label-eyebrow text-white/40 block mb-2">Description</label>
          <textarea rows={3} className="input-sharp" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="event-description" />
        </div>
        <div className="md:col-span-2">
          <button type="submit" disabled={saving} className="btn-primary" style={{ background: "#fff", color: "#000", borderColor: "#fff" }} data-testid="event-save">
            {saving ? "Saving…" : editingId ? "Save changes" : "Add event"}
          </button>
        </div>
      </form>

      <div className="border border-white/10 bg-[#141414] overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-[#0A0A0A] text-white/60"><tr><Th>Date</Th><Th>Title</Th><Th>Service</Th><Th>Venue</Th><Th>From</Th><Th>Actions</Th></tr></thead>
          <tbody>
            {items.map((ev) => (
              <tr key={ev.id} className="border-t border-white/10" data-testid={`admin-event-${ev.id}`}>
                <Td className="font-mono text-xs">{ev.event_date?.replace("T", " ").slice(0, 16)}</Td>
                <Td>{ev.title}</Td>
                <Td className="capitalize">{ev.service}</Td>
                <Td>{ev.venue || "—"}</Td>
                <Td>{ev.ticket_from != null ? `GHS ${Number(ev.ticket_from).toFixed(2)}` : "—"}</Td>
                <Td>
                  <div className="flex gap-1">
                    <button onClick={() => edit(ev)} className="text-[11px] px-2 py-1 border border-white/30 text-white/70 hover:bg-white hover:text-black" data-testid={`event-edit-${ev.id}`}>Edit</button>
                    <button onClick={() => remove(ev)} className="text-[11px] px-2 py-1 border border-white/30 text-white/60 hover:bg-white hover:text-black inline-flex items-center gap-1" data-testid={`event-delete-${ev.id}`}>
                      <Trash size={12} /> Delete
                    </button>
                  </div>
                </Td>
              </tr>
            ))}
            {items.length === 0 && <tr><Td className="text-white/40" colSpan={6}>No events yet — use the form above to add the first fight night.</Td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Inp({ label, value, onChange, type = "text", testid, required, step }) {
  return (
    <div>
      <label className="label-eyebrow text-white/40 block mb-2">{label}{required && " *"}</label>
      <input
        type={type}
        step={step}
        required={required}
        className="input-sharp"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </div>
  );
}


const Th = ({ children }) => <th className="text-left p-3 font-semibold text-xs uppercase tracking-wider">{children}</th>;
const Td = ({ children, className = "", colSpan }) => <td colSpan={colSpan} className={`p-3 ${className}`}>{children}</td>;
